import { List, Map } from 'immutable'
import { createSelector } from 'reselect'

import { CARE_LANGUAGES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { TREATMENT_OPTIONS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'

import { isImmutable } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/immutableUtils'
import { blankAddressSearch } from '@lyrahealth-inc/shared-app-logic/src/features/locationFinder/constants'
import { defaultNextHiddenProviderIndex } from '@lyrahealth-inc/shared-app-logic/src/features/providerLevelFeedback/constants'
import { ProviderTypeAbbreviated } from '@lyrahealth-inc/shared-app-logic/src/features/providers/constants'
import {
  getProviderSupportsLiveMessaging,
  getShouldDisplayBCTProviderBenefits as getShouldDisplayBCTProviderBenefitsUtil,
  isBCMProvider,
  isBlendedCareProvider,
  isDAProviderOfferingMeds,
} from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { DirectLinkSource } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { computePersistentOnsiteEntrypointSadPath } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/hooks/useOnsiteTherapyVisibility'
import { isCarelonOutpatientTherapyTreatmentOption } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { WELLNESS_TOOLKIT_RETURN_DESTINATION } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/types'
import { BookingMode } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { ProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { getIsOnsiteEligible, getLoadingOnsiteProviders, getT1OnsiteProvidersAddresses } from './t1-flow/t1Selectors'
import { getTriageIsSearchingForAdult } from './triage-demographics/triageDemographicsSelectors'
import { logToSentry } from '../../../common/utils/userUtils'
import { getLiveMessagingEnabled, getShouldDisplayBCTProviderBenefits } from '../../../data/customer/customerSelectors'
import {
  getIsOnsiteEligibleResponseEnabled,
  getIsOnsiteEntrypointsEnabled,
} from '../../../data/featureFlags/featureFlagSelectors'
import { getIsUserInternational } from '../../../data/user/userSelectors'

export const getOnboardState = (state: Map<string, any>) => state?.get('onboard')

export const getOnboardData = createSelector(getOnboardState, (onboardState) => onboardState?.get('data'))

export const getOnboardMeetingFormat = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('meetingFormat'),
)

export const getOnboardCurrentMeetingFormatFilter = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('currentMeetingFormatFilter'),
)

export const getOnboardSearchCount = createSelector(getOnboardData, (onboardData) => onboardData?.get('searchCount', 0))

// If the user has not yet entered a search location, use the browser's guessed time zone.
export const getOnboardTimezoneForAvailabilityRequest = createSelector(
  getOnboardData,
  (onboardData) =>
    onboardData?.get('timeZoneForAvailabilityRequest') ?? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
)

export const getOnboardLocation = createSelector(getOnboardState, (onboardState) => onboardState?.get('location'))

export const getLoadingUserFavoriteProviders = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('loadingUserFavoriteProviders'),
)

export const getLatestUserFavoriteProviders = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('latestUserProviders'),
)

export const getLatestMatchesSearch = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('latestMatchesSearch'),
)

export const getIsLatestMatchesCleared = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('isLatestMatchesCleared'),
)

export const getLatestMatchesSearchDate = createSelector(getLatestMatchesSearch, (latestMatchesSearch) => {
  const selfSearchTriageUpdateDate = latestMatchesSearch?.update_date ?? latestMatchesSearch?.create_date
  return selfSearchTriageUpdateDate
})

export const getLatestUserFavoriteProviderTreatmentType = createSelector(
  [getLatestUserFavoriteProviders],
  (latestUserProviders) => latestUserProviders[latestUserProviders.length - 1]?.treatmentPreference,
)

export const getIsLatestMatches = createSelector(getLatestMatchesSearch, (latestMatchesSearch) => !!latestMatchesSearch)

export const getLoadingLatestProviderMatches = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('loadingLatestProviderMatches'),
)

export const getOnboardLocationSelectedAddressOption = createSelector(getOnboardLocation, (onboardLocation) =>
  onboardLocation?.get('selectedAddressOption'),
)

export const getOnboardLocationSelectedDropdownValue = createSelector(getOnboardLocation, (onboardLocation) =>
  onboardLocation?.get('selectedDropdownValue'),
)

export const getOnboardIsLocationOnsite = createSelector(getOnboardLocation, (onboardLocation) =>
  Boolean(onboardLocation?.get('onsite')),
)

export const getOnboardLocationAddress = createSelector(
  getOnboardLocation,
  (onboardLocation) => onboardLocation?.get('address') ?? Map(),
)

export const getOnboardLocationGeographyData = createSelector(
  getOnboardLocation,
  (onboardLocation) => onboardLocation?.get('geo') ?? Map(blankAddressSearch),
)

export const getOnboardLocationLat = createSelector(getOnboardLocationGeographyData, (geoData) =>
  geoData?.get('lat', ''),
)

export const getOnboardLocationLon = createSelector(getOnboardLocationGeographyData, (geoData) =>
  geoData?.get('lon', ''),
)

export const getOnboardLocationStreet1 = createSelector(getOnboardLocationGeographyData, (geoData) =>
  geoData?.get('street1', ''),
)

export const getOnboardLocationZipcode = createSelector(
  getOnboardLocationGeographyData,
  (geoData) => geoData?.get('zip', '') || geoData?.get('zipcode', ''),
)

export const getOnboardLocationUserInput = createSelector(getOnboardLocationGeographyData, (geoData) =>
  geoData?.get('userInput'),
)

export const getOnboardLocationCountry = createSelector(
  getOnboardLocationGeographyData,
  (geoData) => geoData?.get('country') ?? '',
)

export const getOnboardLocationState = createSelector(
  getOnboardLocationGeographyData,
  (geoData) => geoData?.get('state') ?? '',
)

export const getOnboardFormattedAddress = createSelector(getOnboardLocationGeographyData, (geoData) =>
  geoData?.get('formattedAddress'),
)

export const getOnboardOnsiteName = createSelector(getOnboardLocation, (onboardLocation) =>
  onboardLocation?.get('onsiteName'),
)

export const getOnboardIsLocationNear = createSelector(getOnboardLocation, (onboardLocation) =>
  Boolean(onboardLocation?.get('near')),
)

const getOnboardProviderProfile = createSelector(getOnboardState, (onboardState) =>
  onboardState?.get('providerProfile'),
)

export const getOnboardProviderProfileData = createSelector(
  getOnboardProviderProfile,
  (providerProfile) => providerProfile?.get('data') ?? Map(),
)

export const getOnboardProviderProfileDisplayName = createSelector(
  getOnboardProviderProfileData,
  (providerProfileData) => providerProfileData?.get('display_name', ''),
)

export const getIsOnboardProviderOnsite = createSelector(
  getOnboardProviderProfileData,
  (providerProfileData) => providerProfileData?.get('onsite', false) ?? false,
)

export const getOnboardProviderNearestAddress = createSelector(
  getOnboardProviderProfileData,
  (providerProfileData) => providerProfileData?.get('nearestAddress', Map()) ?? Map(),
)

export const getOnboardProviderAddresses = createSelector(
  getOnboardProviderProfileData,
  (providerProfileData) => providerProfileData?.get('addresses', List()) ?? List(),
)

export const getOnboardProviderProfileDataJS = createSelector(
  getOnboardProviderProfileData,
  (providerProfileData) => providerProfileData?.toJS() as ProviderInfo | undefined,
)

const getIndividualTherapyState = createSelector(getOnboardState, (onboardState) =>
  onboardState?.get('individualTherapy'),
)

export const getShouldShowIndividualTherapyModal = createSelector(getIndividualTherapyState, (individualTherapy) =>
  Boolean(individualTherapy?.get('showModal')),
)

export const getOnboardProviderProfileLanguages = createSelector(
  getOnboardProviderProfileData,
  (onboardProviderProfileData) => onboardProviderProfileData?.get('languages_bcp47') ?? List(),
)

export const getOnboardProviderCapacity = createSelector(
  getOnboardProviderProfileData,
  (onboardProviderProfileData) => onboardProviderProfileData?.get('providerCapacity') ?? Map(),
)

export const getOnboardProviderDisplayName = createSelector(
  getOnboardProviderProfileData,
  (onboardProviderProfileData) => onboardProviderProfileData?.get('display_name'),
)

export const getOnboardProviderModality = createSelector(
  getOnboardProviderProfileData,
  (onboardProviderProfileData) => onboardProviderProfileData?.get('modalities') ?? List(),
)

export const getOnboardProviderLyraId = createSelector(getOnboardProviderProfileData, (onboardProviderProfileData) =>
  onboardProviderProfileData?.get('lyra_id'),
)

export const getOnboardProviderImageUrl = createSelector(getOnboardProviderProfileData, (onboardProviderProfileData) =>
  onboardProviderProfileData?.getIn(['lyraProviderImage', 'imageUrl'], ''),
)

export const getOnboardProviderTaxonomyData = createSelector(
  getOnboardProviderProfileData,
  (onboardProviderProfileData) => onboardProviderProfileData?.get('program_taxonomy'),
)

export const getOnboardProviderType = createSelector([getOnboardProviderTaxonomyData], (programTaxonomyImmutable) => {
  const programTaxonomy = programTaxonomyImmutable?.toJS()
  return programTaxonomy
    ? isBlendedCareProvider({ programTaxonomy })
      ? ProviderTypeAbbreviated.BC
      : ProviderTypeAbbreviated.DA
    : undefined
})

export const getSelectedProviderOffersMedManagement = createSelector(
  getOnboardProviderTaxonomyData,
  (programTaxonomyImmutable) => {
    const programTaxonomy = programTaxonomyImmutable?.toJS()
    return programTaxonomy
      ? isDAProviderOfferingMeds({ programTaxonomy }) || isBCMProvider({ programTaxonomy })
      : undefined
  },
)

export const getShouldDisplayBCTProviderBenefitsForProvider = createSelector(
  getOnboardProviderProfileData,
  (state: any) => getShouldDisplayBCTProviderBenefits(state),
  (provider: any, shouldDisplayBCTProviderBenefits: any) => {
    return getShouldDisplayBCTProviderBenefitsUtil({
      isCustomerDisplayingBCTBenefits: shouldDisplayBCTProviderBenefits,
      provider: isImmutable({ value: provider }) ? (provider as Map<string, any>).toJS() : provider,
    })
  },
)

export const getOnboardProviderCostInfo = createSelector(getOnboardProviderProfileData, (onboardProviderProfileData) =>
  onboardProviderProfileData?.get('costInfo'),
)

export const getOnboardProviderProfileVideoLink = createSelector(
  getOnboardProviderProfileData,
  (onboardProviderProfileData) => onboardProviderProfileData?.get('profileVideo')?.get('videoLink'),
)

export const getOnboardProviderTotalSessionCost = createSelector(getOnboardProviderCostInfo, (costInfo) =>
  costInfo?.get('total_session_cost'),
)

const getOnboardTreatmentOptions = createSelector(getOnboardState, (onboardState) =>
  onboardState?.get('treatmentOptions'),
)

export const getOnboardSelectedTreatmentOption = createSelector(
  getOnboardTreatmentOptions,
  (onboardTreatmentOptions: $TSFixMe) => onboardTreatmentOptions?.get('selectedTreatmentOption'),
)

export const getOnboardFallbackTreatmentOption = createSelector(getOnboardTreatmentOptions, (onboardTreatmentOptions) =>
  onboardTreatmentOptions?.get('fallbackTreatmentOption'),
)

export const getOnboardSelectedTreatmentOptionCTPO = createSelector(
  getOnboardTreatmentOptions,
  (onboardTreatmentOptions) => onboardTreatmentOptions?.get('selectedTreatmentOptionCTPO'),
)

export const getOnboardIsSelectedTreatmentOptionAUD = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) => treatmentOption === TREATMENT_OPTIONS.ALCOHOL_USE_DISORDER,
)

export const getOnboardIsSelectedTreatmentOptionCoaching = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) =>
    treatmentOption === TREATMENT_OPTIONS.COACHING ||
    treatmentOption === TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING,
)

export const getOnboardProviderSupportsLiveMessaging = createSelector(
  (state: Map<string, any>) => getLiveMessagingEnabled(state),
  getOnboardProviderProfileData,
  getOnboardSelectedTreatmentOption,
  (liveMessagingEnabled, providerInfo, selectedTreatmentOption) =>
    liveMessagingEnabled &&
    getProviderSupportsLiveMessaging({ provider: providerInfo.toJS(), selectedTreatmentOption }),
)

const getOnboardIsSelectedTreatmentOptionIndividualTherapy = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) => treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
)

const getOnboardIsSelectedTreatmentOptionCouplesTherapy = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) => treatmentOption === TREATMENT_OPTIONS.COUPLES_THERAPY,
)

const getOnboardIsSelectedTreatmentOptionFamilyTherapy = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) => treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY,
)

export const getOnboardIsSelectedTreatmentOptionBlendedCareMeds = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) => treatmentOption === TREATMENT_OPTIONS.BLENDED_CARE_MEDS,
)

// This treatment option comes from the MedsOptionsExplained page
export const getOnboardIsSelectedTreatmentOptionMedicationConsultation = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) => treatmentOption === TREATMENT_OPTIONS.MEDICATION_CONSULTATION,
)

export const getOnboardIsSelectedTreatmentOptionDirectAccessMeds = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) =>
    treatmentOption === TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS ||
    treatmentOption === TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS_CHILD,
)

// This treatment option comes from the MedsOptionsExplained page
export const getOnboardIsSelectedTreatmentOptionControlledMedication = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) => treatmentOption === TREATMENT_OPTIONS.CONTROLLED_MEDICATION,
)

export const getIsSearchingForTherapy = createSelector(
  getOnboardIsSelectedTreatmentOptionIndividualTherapy,
  getOnboardIsSelectedTreatmentOptionCouplesTherapy,
  getOnboardIsSelectedTreatmentOptionFamilyTherapy,
  (seekingIndividualTherapy, seekingCouplesTherapy, seekingFamilyTherapy) => {
    return seekingIndividualTherapy || seekingCouplesTherapy || seekingFamilyTherapy
  },
)

export const getIsCarelonOutpatientTherapySearch = createSelector(
  getOnboardSelectedTreatmentOption,
  isCarelonOutpatientTherapyTreatmentOption,
)

export const getIsSelfCarelonOutpatientTherapySearch = createSelector(
  getIsCarelonOutpatientTherapySearch,
  getTriageIsSearchingForAdult,
  (isCarelonOutpatientTherapySearch, isSearchingForSelf) => isCarelonOutpatientTherapySearch && isSearchingForSelf,
)

export const getIsSearchingForAdultTherapy = createSelector(
  getIsSearchingForTherapy,
  getTriageIsSearchingForAdult,
  (isSearchingForTherapy, isSearchingForAdult) => {
    return isSearchingForAdult && isSearchingForTherapy
  },
)

export const getIsSearchingForChildTherapy = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) =>
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD,
)

export const getIsSearchingForAdultMeds = createSelector(
  getOnboardSelectedTreatmentOption,
  (treatmentOption) =>
    treatmentOption === TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS ||
    treatmentOption === TREATMENT_OPTIONS.BLENDED_CARE_MEDS ||
    treatmentOption === TREATMENT_OPTIONS.CONTROLLED_MEDICATION ||
    treatmentOption === TREATMENT_OPTIONS.MEDICATION_CONSULTATION,
)

export const getShouldUseRelevantClinicalSpecializationsHeader = createSelector(
  getIsSearchingForTherapy,
  getIsSearchingForChildTherapy,
  getOnboardIsSelectedTreatmentOptionCoaching,
  (isSearchingForTherapy, isSearchingForChildTherapy, isSelectedTreatmentOptionCoaching) => {
    return isSearchingForTherapy || isSearchingForChildTherapy || isSelectedTreatmentOptionCoaching
  },
)

const getOnboardAppointmentBooking = createSelector(getOnboardState, (onboardState) =>
  onboardState?.get('appointmentBooking'),
)

export const getOnboardAppointment = createSelector(
  getOnboardAppointmentBooking,
  (appointmentBooking) => appointmentBooking?.get('appointment') ?? Map(),
)

export const getOnboardAppointmentDate = createSelector(getOnboardAppointment, (appointment) =>
  appointment?.get('date'),
)

export const getOnboardAppointmentTime = createSelector(getOnboardAppointment, (appointment) =>
  appointment?.get('time'),
)

export const getOnboardShouldShowAppointmentModal = createSelector(getOnboardAppointmentBooking, (appointmentBooking) =>
  Boolean(appointmentBooking?.get('showModal')),
)

const getOnboardModalityQuestionnaire = createSelector(getOnboardState, (onboardState) =>
  onboardState?.get('modalityQuestionnaire'),
)

export const getOnboardModalityQuestionnaireRecommendations = createSelector(
  getOnboardModalityQuestionnaire,
  (modalityQuestionnaire) => modalityQuestionnaire?.get('recommendations') ?? List(),
)

export const getOnboardShouldShowSurveyRecommendations = createSelector(
  getOnboardModalityQuestionnaire,
  (modalityQuestionnaire) => Boolean(modalityQuestionnaire?.get('showSurveyRecommendations')),
)

export const getOnboardModalityQuestionnaireAnswers = createSelector(
  getOnboardModalityQuestionnaire,
  (modalityQuestionnaire) => modalityQuestionnaire?.get('answers') ?? List(),
)

export const getOnboardModalityQuestionnaireSurveyData = createSelector(
  getOnboardModalityQuestionnaire,
  (modalityQuestionnaire) => modalityQuestionnaire?.get('surveyData'),
)

export const getOnboardModalityQuestionGroups = createSelector(
  getOnboardModalityQuestionnaireSurveyData,
  (surveyData) => surveyData?.get('modalityQuestionGroups'),
)

export const createGetOnboardModalityQuestionGroupsWithIndex = ({ index }: { index: number }) => {
  return createSelector(getOnboardModalityQuestionGroups, (questionGroups) => questionGroups?.get(index) ?? Map())
}

const getOnboardProviders = createSelector(getOnboardState, (onboardState) => onboardState?.get('providers'))

export const getOnboardProvidersDisplayed = createSelector(getOnboardProviders, (providers) => {
  const onboardProviders = providers?.get('providersDisplayed') ?? List()
  let providerList: ProviderInfo[] = []

  onboardProviders.forEach(
    (onboardProviderObj: { bucketName: string; providerList: ProviderInfo[] } | ProviderInfo) => {
      if ('bucketName' in onboardProviderObj) {
        providerList = providerList.concat(
          (onboardProviderObj as { bucketName: string; providerList: ProviderInfo[] }).providerList,
        )
      } else {
        providerList = providerList.concat(onboardProviderObj)
      }
    },
  )
  return providerList
})

export const getOnboardTotalNumberOfProviders = createSelector(getOnboardProviders, (providers) => {
  const total = providers?.get('totalNumberOfProviders')
  return parseInt(total ?? 0, 10)
})

export const getOnboardNextHiddenProviderIndex = createSelector(getOnboardProviders, (providers) =>
  providers?.get('nextHiddenProviderIndex', defaultNextHiddenProviderIndex),
)

export const getOnboardUserFavoriteProviderRequestId = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('userFavoriteProviderRequestId'),
)

export const getIsSelectedProviderTopProvider = createSelector(
  getOnboardProviderLyraId,
  getOnboardProvidersDisplayed,
  (selectedProviderLyraId, displayedProviders) => {
    if (!displayedProviders || displayedProviders?.length < 2) {
      return false
    }
    return displayedProviders.findIndex((provider) => provider.lyra_id === selectedProviderLyraId) === 0
  },
)

const getOnboardProvidersTopProviderData = createSelector(getOnboardProvidersDisplayed, (providers) => {
  if (!providers?.length) {
    return null
  }
  return providers[0]
})

export const getOnboardIsTopProviderRecommended = createSelector(getOnboardProvidersTopProviderData, (provider) => {
  return provider?.testimonialData?.isRecommended
})

const getOnboardTriageHealthPlan = createSelector(getOnboardState, (onboard) => onboard?.get('triageHealthPlan'))

const getOnboardSelectedHealthPlan = createSelector(getOnboardTriageHealthPlan, (triageHealthPlan) =>
  triageHealthPlan?.get('selectedHealthPlan'),
)

export const getOnboardSelectedHealthPlanName = createSelector(getOnboardSelectedHealthPlan, (healthPlan) =>
  healthPlan?.get('planName'),
)

export const getOnboardAddressData = createSelector(
  getOnboardLocationAddress,
  getIsOnboardProviderOnsite,
  getOnboardProviderNearestAddress,
  (onsiteAddress, isProviderOnsite, providerNearestAddress) => {
    return isProviderOnsite && onsiteAddress.size > 0 ? onsiteAddress : providerNearestAddress
  },
)

export const getOnboardShouldDisplayCarePreferencesPage = createSelector(
  (state: $TSFixMe) => getIsUserInternational(state),
  getIsSearchingForAdultTherapy,
  getOnboardIsSelectedTreatmentOptionIndividualTherapy,
  (isUserInternational: $TSFixMe, isSearchingForAdultTherapy: $TSFixMe, searchingForIndividualTherapy: $TSFixMe) => {
    if (isUserInternational) {
      return searchingForIndividualTherapy
    }

    return isSearchingForAdultTherapy
  },
)

export const getOnboardIsValidMemberPreferencesTreatmentOption = createSelector(
  getIsSearchingForAdultTherapy,
  getOnboardIsSelectedTreatmentOptionCoaching,
  getIsSearchingForChildTherapy,
  (isSearchingForAdultTherapy: boolean, isSearchingForCoaching: boolean, isSearchingForChildTherapy: boolean) => {
    return isSearchingForAdultTherapy || isSearchingForCoaching || isSearchingForChildTherapy
  },
)

const getOnboardFavoritedProvidersStore = createSelector(getOnboardState, (onboardState) =>
  onboardState?.get('favoritedProviders'),
)

export const getOnboardFavoritedProvidersByIds = createSelector(
  getOnboardFavoritedProvidersStore,
  (favoritedProvidersStore) => favoritedProvidersStore?.get('favoritedProvidersByIds'),
)

export const getOnboardHasFavoritedProviders = createSelector(
  getOnboardFavoritedProvidersByIds,
  (favoritedProvidersByIds) => favoritedProvidersByIds && favoritedProvidersByIds.length > 0,
)

export const getIsOnboardProviderFavoritedProvider = createSelector(
  [getOnboardProviderLyraId, getOnboardFavoritedProvidersByIds],
  (selectedProviderLyraId, favoritedProvidersByIds) => {
    if (!selectedProviderLyraId || !Array.isArray(favoritedProvidersByIds)) return false
    return favoritedProvidersByIds.includes(selectedProviderLyraId)
  },
)

export const getOnboardFavoritedProvidersMetadataMap = createSelector(
  getOnboardFavoritedProvidersStore,
  (favoritedProvidersStore) => favoritedProvidersStore?.get('favoritedProvidersMetadataMap'),
)

const getOnboardCarePreferences = createSelector(getOnboardState, (onboardState) =>
  onboardState?.get('carePreferences'),
)

export const getOnboardAvailableCareLanguages = createSelector(getOnboardCarePreferences, (carePreferences) => {
  const codes: string[] = carePreferences?.get('availableCareLanguages') ?? []
  const unknownCodes = codes.filter((code: string) => !(code in CARE_LANGUAGES))
  if (unknownCodes.length > 0) {
    logToSentry(
      'careLanguages/unknownCodes',
      {
        message: `Unknown care language codes not defined in CARE_LANGUAGES: ${unknownCodes.join(', ')}`,
        unknownCodes,
      },
      'error',
    )
  }
  return codes.filter((code: string) => code in CARE_LANGUAGES)
})

export const getOnboardSelectedCareLanguage = createSelector(getOnboardCarePreferences, (carePreferences) =>
  carePreferences?.get('selectedCareLanguage'),
)

export const getOnboardProviderProfileAvailabilityCS = createSelector(
  getOnboardProviderProfileData,
  (onboardProviderProfileData) => onboardProviderProfileData?.get('availabilityCS'),
)

export const getOnboardProviderProfileAvailabilityCSFirstAvailabilityDateObject = createSelector(
  getOnboardProviderProfileAvailabilityCS,
  (onboardProviderProfileAvailabilityCS) => {
    const firstDate = onboardProviderProfileAvailabilityCS
      ?.get('provider_availability')
      ?.get(0)
      ?.get('availability')
      ?.get(0)
      ?.get('start')
    if (!firstDate) {
      return undefined
    }

    return new Date(firstDate)
  },
)

const getOnboardProviderProfileAvailabilityCSBookingMode = createSelector(
  getOnboardProviderProfileAvailabilityCS,
  (onboardProviderProfileAvailabilityCS) => {
    return onboardProviderProfileAvailabilityCS?.get('provider_availability')?.get(0)?.get('booking_mode')
  },
)

export const getOnboardProviderProfileAvailabilityCSBookingModeIsOnIPC = createSelector(
  getOnboardProviderProfileAvailabilityCSBookingMode,
  (bookingMode) => bookingMode === BookingMode.HYBRID,
)

export const getUserMemberPreferencesV2 = createSelector(getOnboardCarePreferences, (carePreferences) =>
  carePreferences?.get('userMemberPreferencesV2'),
)

export const getIsParentInitiatedInPersonS4CPreferenceChecked = createSelector(
  getOnboardCarePreferences,
  (carePreferences) => carePreferences?.get('isParentInitiatedInPersonS4CPreferenceChecked'),
)

export const getIsParentInitiatedInPersonTeenS4CPreferenceChecked = createSelector(
  getOnboardCarePreferences,
  (carePreferences) => carePreferences?.get('isParentInitiatedInPersonTeenS4CPreferenceChecked'),
)

export const getOnboardParsePreference = createSelector(getOnboardCarePreferences, (carePreferences) =>
  carePreferences?.get('parsePreference'),
)

export const getParsePreferenceId = createSelector(
  getOnboardParsePreference,
  (parsePreference): string | null => parsePreference?.get('id') ?? null,
)

export const getOnboardIsCurrentSearchDirectPath = createSelector(getOnboardData, (onboardData: $TSFixMe) =>
  onboardData?.get('isCurrentSearchDirectPath'),
)

export const getOnboardDirectLinkIntent = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('directLinkIntent'),
)

/** The teen a parent is booking for in the searchless direct-booking flow (ACCOUNT-4310). */
export const getDirectBookingChildLyraId = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('directBookingChildLyraId'),
)

export const getOnboardDirectLinkSource = createSelector(getOnboardData, (onboardData): DirectLinkSource | undefined =>
  onboardData?.get('directLinkSource'),
)

export const getOnboardDirectLinkUtmSource = createSelector(getOnboardData, (onboardData): string | undefined =>
  onboardData?.get('directLinkUtmSource'),
)

export const getOnboardToolkitType = createSelector(getOnboardData, (onboardData) => onboardData?.get('toolkitType'))

export const getOnboardOneLyraToolkitReturnDestination = createSelector(
  getOnboardData,
  (onboardData): WELLNESS_TOOLKIT_RETURN_DESTINATION | undefined =>
    onboardData?.get('oneLyraToolkitReturnDestination') ?? undefined,
)

export const getCareOptionsCarouselIndex = createSelector(
  getOnboardData,
  (onboardData) => onboardData?.get('careOptionsCarouselIndex') ?? 0,
)

export const getOnboardProviderProfileBioKeywords = createSelector(
  getOnboardProviderProfileData,
  (onboardProviderProfileData) => onboardProviderProfileData?.get('provider_descriptors'),
)

export const getOnboardWellnessGoals = createSelector(
  getOnboardData,
  (onboardData) => onboardData?.get('wellnessGoals') ?? undefined,
)

export const getUserSearchLocationId = createSelector(getOnboardData, (onboardData) =>
  onboardData?.get('userSearchLocationId'),
)

const getOnboardProviderTestimonialData = createSelector(getOnboardProviderProfileData, (onboardData) =>
  onboardData?.get('testimonialData'),
)

export const getOnboardProviderIsRecommended = createSelector(getOnboardProviderTestimonialData, (testimonialData) =>
  testimonialData?.get('isRecommended'),
)

export const getOnboardProviderTestimonialAdjectives = createSelector(
  getOnboardProviderTestimonialData,
  (testimonialData) => testimonialData?.get('providerAdjectives'),
)

export const getShowPersistentOnsiteEntrypointSadPath = createSelector(
  [
    getIsOnsiteEligibleResponseEnabled,
    getIsOnsiteEligible,
    getIsOnsiteEntrypointsEnabled,
    getT1OnsiteProvidersAddresses,
    getOnboardDirectLinkSource,
    getOnboardIsCurrentSearchDirectPath,
    getLoadingOnsiteProviders,
  ],
  (
    isOnsiteEligibleResponseEnabled,
    isOnsiteEligible,
    isOnsiteEntrypointsEnabled,
    onsiteProviders,
    directLinkSource,
    isDirectPath,
    loading,
  ): boolean => {
    return computePersistentOnsiteEntrypointSadPath({
      isOnsiteEligibleResponseEnabled,
      isOnsiteEligible,
      isOnsiteEntrypointsEnabled,
      onsiteProvidersCount: onsiteProviders?.size,
      directLinkSource,
      isDirectPath,
      loading,
    })
  },
)
